"use client";

import type { CSSProperties } from "react";
import { alpha, createTheme } from "@/components/controls/base-controls";
import { michroma, playfair, roboto } from "./fonts";
import { colors } from "./tokens";

// import { customMixins } from "./mixins";

// MUI's Default Breakpoints & Values
// xs: Extra-small (0px and up)
// sm: Small (600px and up)
// md: Medium (900px and up)
// lg: Large (1200px and up)
// xl: Extra-large (1536px and up)

declare module "@mui/material/Button" {
	interface ButtonPropsVariantOverrides {
		button: true;
		darkBg: true;
		lightBg: true;
		link: true;
		square: true;
		submit: true;
	}
}

declare module "@mui/material/styles" {
	interface PaletteColor {
		headerBg?: string;
		lightBg?: string;
		fadedBg?: string;
	}
	interface SimplePaletteColorOptions {
		headerBg?: string;
		lightBg?: string;
		fadedBg?: string;
	}
	interface Mixins {
		bgImage: (params: {
			xs: { src: string; height: string };
			sm: { src: string; height: string };
			md: { src: string; height: string };
			lg: { src: string; height: string };
			xl: { src: string; height: string };
			backgroundPosition?: string;
		}) => CSSProperties;
	}
}

const baseTheme = createTheme();

const theme = createTheme({
	mixins: {
		bgImage: ({ xs, sm, md, lg, xl, backgroundPosition = "center top" }) => ({
			backgroundImage: `url(${xs.src})`,
			backgroundRepeat: "no-repeat",
			backgroundSize: "cover",
			backgroundPosition,
			height: xs.height,
			width: "100%",
			[baseTheme.breakpoints.up("sm")]: {
				backgroundImage: `url(${sm.src})`,
				backgroundPosition,
				height: sm.height,
			},
			[baseTheme.breakpoints.up("md")]: {
				backgroundImage: `url(${md.src})`,
				backgroundPosition,
				height: md.height,
			},
			[baseTheme.breakpoints.up("lg")]: {
				backgroundImage: `url(${lg.src})`,
				backgroundPosition,
				height: lg.height,
			},
			[baseTheme.breakpoints.up("xl")]: {
				backgroundImage: `url(${xl.src})`,
				backgroundPosition,
				height: xl.height,
			},
		}),
	},
	palette: {
		error: {
			main: colors.error,
		},
		info: {
			main: colors.info,
		},
		primary: {
			headerBg: colors.headerBg,
			lightBg: colors.lightBg,
			fadedBg: colors.fadedBg,
			main: colors.dark,
		},
		secondary: {
			main: colors.secondary,
		},
	},
	typography: {
		fontFamily: [
			roboto.style.fontFamily,
			playfair.style.fontFamily,
			michroma.style.fontFamily,
		].join(","),
		body1: {
			fontSize: "1rem",
		},
		body2: {
			fontSize: ".875rem",
		},
		error: {
			fontSize: ".875em",
			color: colors.errorAlt,
			fontWeight: 500,
		},
		h1: {
			fontFamily: playfair.style.fontFamily,
			fontSize: "3rem",
			fontWeight: 500,
			[`${baseTheme.breakpoints.up("md")} and (orientation: portrait)`]: {
				fontSize: "4.5rem",
			},
			[`${baseTheme.breakpoints.up("lg")}`]: {
				fontSize: "4.5rem",
			},
		},
		h2: {
			fontFamily: playfair.style.fontFamily,
			fontSize: "1.8rem",
			fontWeight: 500,
			[`${baseTheme.breakpoints.up("md")} and (orientation: portrait)`]: {
				fontSize: "2.5rem",
			},
			[`${baseTheme.breakpoints.up("lg")}`]: {
				fontSize: "2.5rem",
			},
		},
		h3: {
			fontFamily: playfair.style.fontFamily,
			fontSize: "1.25rem",
			fontWeight: 500,
			[`${baseTheme.breakpoints.up("md")} and (orientation: portrait)`]: {
				fontSize: "1.5rem",
			},
			[`${baseTheme.breakpoints.up("lg")}`]: {
				fontSize: "1.5rem",
			},
		},
		h3Policy: {
			fontSize: "1rem",
			fontWeight: 300,
			margin: ".5em 0",
			[`${baseTheme.breakpoints.up("md")} and (orientation: portrait)`]: {
				fontSize: "1.25rem",
			},
			[`${baseTheme.breakpoints.up("lg")}`]: {
				fontSize: "1.25rem",
			},
		},
		banner: {
			fontFamily: playfair.style.fontFamily,
			fontSize: "3.5rem",
			fontWeight: 400,
			[`${baseTheme.breakpoints.up("md")} and (orientation: portrait)`]: {
				fontSize: "6rem",
			},
			[`${baseTheme.breakpoints.up("lg")}`]: {
				fontSize: "6rem",
			},
		},
		companyName: {
			fontFamily: michroma.style.fontFamily,
			fontSize: ".75rem",
			fontWeight: 400,
			[baseTheme.breakpoints.up("md")]: {
				fontSize: "1rem",
			},
		},
		footer: {
			fontFamily: roboto.style.fontFamily,
			fontSize: ".5rem",
			fontWeight: 400,
			[baseTheme.breakpoints.up("md")]: {
				fontSize: ".875rem",
			},
		},
		tagLine: {
			fontFamily: roboto.style.fontFamily,
			fontSize: ".5rem",
			fontWeight: 400,
			[baseTheme.breakpoints.up("md")]: {
				fontSize: ".875rem",
			},
		},
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: (theme) => ({
				":root": {
					"--mui-palette-background-default": colors.backgroundDefault,
					"--mui-palette-bright": colors.bright,
					"--mui-palette-button-link": colors.buttonLink,
					"--mui-palette-checkbox-checked": colors.checkboxChecked,
					"--mui-palette-checkbox-font-color": colors.checkboxFontColor,
					"--mui-palette-dark": theme.palette.primary.main,
					"--mui-palette-darkest": colors.darkest,
					"--mui-palette-dialog-title-bg": colors.dialogTitleBg,
					"--mui-palette-error": colors.errorAlt,
					"--mui-palette-header-bg": colors.headerBg,
					"--mui-palette-header-no-auth-bg": colors.lightest,
					"--mui-palette-dull": colors.dull,
					"--mui-palette-light": colors.light,
					"--mui-palette-lightest": colors.lightest,
					"--mui-palette-link": colors.link,
					"--mui-palette-link-highlight": colors.linkHighlight,
					"--mui-palette-list-title-bgcolor": colors.listTitleBgcolor,
					"--mui-palette-nav-active-bgcolor": colors.navActiveBgcolor,
					"--mui-palette-nav-highlight": colors.navHighlight,
					"--mui-palette-option-bg": colors.optionBg,
					"--mui-palette-page-bg": colors.pageBg,
					"--mui-palette-selected-listitem-bg-color":
						colors.selectedListitemBgColor,
					"--mui-palette-submit-bg": colors.submitBg,
					"--mui-palette-success": colors.success,
				},
				body: {
					backgroundColor: "var(--mui-palette-page-bg)",
					color: "#000",
					margin: 0,
					padding: 0,
				},
			}),
		},
		MuiButton: {
			variants: [
				{
					props: { variant: "link" },
					style: {
						border: `2px solid ${colors.buttonLink}`,
						borderRadius: 40,
						color: colors.buttonLink,
						letterSpacing: "0.2em",
						padding: "1.2em 2.2em",
						textTransform: "none",
						width: "fit-content",
						"&:hover": {
							backgroundColor: colors.buttonLink,
							color: colors.lightest,
						},
					},
				},
				{
					props: { variant: "lightBg" },
					style: {
						backgroundColor: "var(--mui-palette-page-bg)",
						border: "1px solid var(--mui-palette-darkest)",
						borderRadius: 40,
						color: "var(--mui-palette-darkest)",
						textTransform: "none",
						"&:hover": {
							backgroundColor: "var(--mui-palette-submit-bg)",
							border: "1px solid var(--mui-palette-submit-bg)",
							color: "var(--mui-palette-light)",
						},
					},
				},
				{
					props: { variant: "darkBg" },
					style: {
						backgroundColor: "var(--mui-palette-darkest)",
						border: "1px solid var(--mui-palette-bright)",
						borderRadius: 40,
						color: "var(--mui-palette-light)",
						textTransform: "none",
						"&:hover": {
							backgroundColor: "var(--mui-palette-bright)",
							color: "var(--mui-palette-darkest)",
						},
					},
				},
			],
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					backgroundColor: "transparent",
					color: "var(--mui-palette-checkbox-font-color)",
					padding: "0",
					"&.Mui-checked": {
						color: "var(--mui-palette-checkbox-checked)",
						backgroundColor: "transparent",
					},
				},
			},
			// You can also set default props here if needed
			// defaultProps: {
			//   disableRipple: true,
			// },
		},
		MuiFilledInput: {
			styleOverrides: {
				root: {
					borderRadius: "5px",
					overflow: "hidden",
					backgroundColor: alpha("#ffffff", 0.5),
					"&:hover": {
						backgroundColor: alpha("#ffffff", 0.7),
						// Reset browser default behavior
						"@media (hover: none)": {
							backgroundColor: "rgba(255, 255, 255, 0.09)",
						},
					},
					"&.Mui-focused": {
						backgroundColor: alpha("#ffffff", 0.8),
					},
					// Style the active active focus line
					"&:after": {
						borderBottomColor: "rgba(255, 255, 255, 0.09)",
					},
				},
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				label: {
					// Adjust the margin-left to control the gap
					marginLeft: ".3em", // Example: 8px gap
					// You can also use theme.spacing for consistent spacing
					// marginLeft: theme.spacing(1), // Example: 1 unit of spacing (default 8px)
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					color: "black",
					"&.Mui-focused": {
						color: "var(--mui-palette-dark)",
					},
				},
			},
		},
		MuiLink: {
			styleOverrides: {
				root: {
					fontSize: 16,
					textDecoration: "none", // Remove default underline for all MuiLink components
					"&:hover": {
						// textDecoration: "underline", // Add underline on hover
						fontWeight: "bold", // Optional: make the text bold on hover
					},
					'&[href^="mailto:"]:hover': {
						color: "var(--mui-palette-link-highlight)",
					},
					// You can also set font size specifically for MuiLink if needed
					// fontSize: '0.9rem',
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "var(--mui-palette-dark)",
					},
				},
				notchedOutline: {
					"& legend": {
						padding: 0,
					},
				},
				input: {
					"&:-webkit-autofill": {
						// Trick the browser by delaying the background color change indefinitely
						WebkitTransition: "background-color 600000s ease-in-out 0s",
						// Maintain your desired text color when autofilled
						WebkitTextFillColor: "#000 !important",
					},
				},
			},
		},
		MuiRadio: {
			styleOverrides: {
				root: {
					backgroundColor: "transparent",
					padding: "0",
					// You can also target specific states if needed
					"&.Mui-checked": {
						backgroundColor: "transparent",
					},
					"&.Mui-focusVisible": {
						backgroundColor: "transparent",
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				head: {
					backgroundColor: "var(--mui-palette-light)", // Light gray shade for the header
					fontWeight: "bold",
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					// Keep layout modifications here (e.g., width, margins)
				},
			},
		},
	},
});

export default theme;
